import type { HTMLAttributes, PropsWithChildren } from "react";

import type { TImageProps } from "@/ui/components/primitives/Image";
import type { DefinedDefaults, TWithDataComponent } from "@/ui/lib/types";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const CALLOUT_ASSET_GRID_CLASS_MAP = {
  default: "",
  withImage: "grid-cols-[theme(spacing.12)_1fr]",
} as const;

export const CALLOUT_ORIENTATION_GRID_CLASS_MAP = {
  default: "grid-rows-[min-content_1fr]",
  reverse: "grid-rows-[1fr_min-content]",
} as const;

export const CALLOUT_COPY_COLUMN_CLASS_MAP = {
  default: "col-start-1",
  withAsset: "col-start-2",
} as const;

export const CALLOUT_VARIANT_CLASS_MAP = {
  default: "bg-component-callout-default",
  reassurance: "bg-component-callout-reassurance",
  subtle: "bg-component-callout-subtle",
  elevated: "bg-component-callout-elevated",
} as const;

export const CALLOUT_TITLE_ROW_START_CLASS_MAP: Record<TCalloutOrientation, string> = {
  default: "row-start-1",
  reverse: "row-start-2",
};

export const CALLOUT_BODY_ROW_START_CLASS_MAP: Record<TCalloutOrientation, string> = {
  default: "row-start-2",
  reverse: "row-start-1",
};

/*-------------------------------------
|  Types
-------------------------------------*/

export type TCalloutVariant = keyof typeof CALLOUT_VARIANT_CLASS_MAP;
export type TCalloutOrientation = keyof typeof CALLOUT_ORIENTATION_GRID_CLASS_MAP;
export type TCalloutAsset = TImageProps;

export type TCalloutAssetProps = {
  asset?: TCalloutAsset;
};

export type TCalloutOrientationProps = {
  orientation?: TCalloutOrientation;
};

type TCalloutBaseProps = TWithDataComponent &
PropsWithChildren &
HTMLAttributes<HTMLDivElement>;

export type TCalloutWrapperProps = TCalloutAssetProps &
TCalloutOrientationProps &
TCalloutBaseProps;

export type TCalloutProps = {
  title: string;
  variant?: TCalloutVariant;
} & TCalloutWrapperProps;

export type TCalloutTitleProps = Pick<
  TCalloutProps,
  "title" | "asset" | "orientation"
>;

export type TCalloutBodyProps = Pick<
  TCalloutProps,
  "asset" | "orientation"
> & PropsWithChildren;

type TCalloutWrapperDefaultProps = DefinedDefaults<
  TCalloutProps,
  "data-component"
>;

type TCalloutDefaultProps = DefinedDefaults<
  TCalloutProps,
  "variant" | "orientation"
>;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const CALLOUT_WRAPPER_DEFAULT_PROPS = {
  "data-component": "CalloutWrapper",
} satisfies TCalloutWrapperDefaultProps;

export const CALLOUT_DEFAULTS = {
  variant: "default",
  orientation: "default",
} satisfies TCalloutDefaultProps;
